/**
 * Área de treinamento dos agentes — equipe Optimale (US-407 · GUS-1001 a 1008 · ADR-0010) — Optimale Grid.
 *
 * Criado em: 17/10/2026 · Última modificação: 19/10/2026 · Versão: 1.2.0
 *
 * Só a equipe Optimale (perfil integrador com função ativa) entra; o backend decide cada ação:
 *
 * | Ação | gestor | validador (do perfil) | implantação | QA |
 * |---|---|---|---|---|
 * | Ver perfis, versões, comparação | todos | os seus | todos | todos |
 * | Nova versão, editar rascunho, enviar para teste | ✔ | ✔ | ✔ | |
 * | Registrar resultado dos testes | | | | ✔ |
 * | Publicar, reverter, decidir sugestões | ✔ | ✔ | | |
 * | Criar perfil, conceder/revogar funções | ✔ | | | |
 *
 * Publicar exige testes aprovados e um autor diferente de quem criou ou editou a versão (segregação de funções).
 * "Testes aprovados" exige a última execução no AMBIENTE CONTROLADO aprovada (GUS-1008): evals de segurança e as
 * perguntas-modelo do perfil, rodadas só contra a operadora de simulação (`python -m treinamento.sandbox executar`).
 * Instruções que ampliam a capacidade do agente (manobrar, ignorar limites…) são recusadas pelo backend.
 * v1.1: contador de caracteres (máx. 200) e ordem das instruções (↑ ↓) no editor (GUS-1003).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ErroApi } from "../api";
import { marcaAtual } from "../marca";

type Equipe = { login: string; funcoes: string[]; validador_de: string[] };
type ResumoPerfil = { perfil: string; versao_publicada: number | null; perguntas: { perguntas: number; pendentes: number };
  sugestoes: { pendente: number; aprovada: number; rejeitada: number; bloqueada: number } };
type Perfil = { nome: string; descricao: string; versao_publicada: number | null; versao_em_andamento: number | null; total_versoes: number };
type Instrucao = { tipo: "personalidade" | "limite"; texto: string };
type Versao = { perfil: string; versao: number; estado: string; criado_por: string; criado_em: string; testes_aprovados: boolean | null;
  testes_por: string | null; testes_observacao: string | null; publicado_por: string | null; publicado_em: string | null; instrucoes: Instrucao[] };
type Sugestao = { id: string; perfil: string; tipo: Instrucao["tipo"]; texto: string; motivo: string; exemplos: string[]; ocorrencias: number;
  estado: string; bloqueio: string | null; criada_em: string; decidido_por: string | null; justificativa: string; texto_aprovado: string | null };
type Funcao = { id: string; login: string; funcao: string; perfil_agente: string | null; concedido_por: string; concedido_em: string; revogado_em: string | null };
type Auditoria = { instante: string; autor: string; evento: string; perfil: string | null; versao: number | null; detalhe: string };
type Aba = "visao" | "perfis" | "sugestoes" | "funcoes";
type PerguntaModelo = { id: string; pergunta: string; esperado: "responder" | "limitar" | "recusar"; ativa: boolean; criado_por: string };
type Execucao = { id: string; instante: string; executado_por: string; empresa_sandbox: string; modelo: string; aprovada: boolean; motivo: string;
  evals_seguranca: { id: string; aprovado: boolean }[]; perguntas: { pergunta_id: string; aprovado: boolean }[]; custo_usd: number };

const NOME_FUNCAO: Record<string, string> = { gestor_sistema: "gestor do sistema", validador: "validador", analista_qa: "analista de QA",
  engenheiro_implantacao: "engenharia de implantação" };
const ESTADO_VERSAO: Record<string, string> = { rascunho: "rascunho", em_teste: "em teste", publicada: "publicada", arquivada: "arquivada" };
const msg = (e: unknown) => (e instanceof ErroApi ? e.message : "falha de rede");

export function Treinamento() {
  const [equipe, setEquipe] = useState<Equipe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aba, setAba] = useState<Aba>("visao");
  const [perfilAberto, setPerfilAberto] = useState<string | null>(null);
  useEffect(() => { api<Equipe>("/v1/treinamento/eu").then(setEquipe).catch((e) => setErro(msg(e))); }, []);
  if (erro) return <section className="tela-treinamento"><h2 className="nx-titulo">Treinamento dos agentes</h2><p className="nx-suave">Acesso negado: {erro}. Área exclusiva da equipe {marcaAtual().empresa}.</p></section>;
  if (!equipe) return <section className="tela-treinamento"><p className="nx-suave">carregando…</p></section>;
  const gestor = equipe.funcoes.includes("gestor_sistema");
  return (
    <section className="tela-treinamento" aria-label="Treinamento dos agentes">
      <header className="cabecalho-paineis">
        <h2 className="nx-titulo">Treinamento dos agentes</h2>
        <span className="nx-suave">{equipe.login} · {equipe.funcoes.map((f) => NOME_FUNCAO[f] ?? f).join(", ")}
          {equipe.validador_de.length ? ` (${equipe.validador_de.join(", ")})` : ""}</span>
        <nav className="sub-abas" aria-label="Treinamento">
          {([["visao", "Visão geral"], ["perfis", "Perfis e versões"], ["sugestoes", "Sugestões"], ...(gestor ? [["funcoes", "Equipe e auditoria"]] : [])] as [Aba, string][])
            .map(([k, t]) => <button key={k} type="button" className={aba === k ? "ativa" : ""} onClick={() => setAba(k)}>{t}</button>)}
        </nav>
      </header>
      {aba === "visao" && <Visao aoAbrir={(p) => { setPerfilAberto(p); setAba("perfis"); }} irSugestoes={() => setAba("sugestoes")} />}
      {aba === "perfis" && <Perfis equipe={equipe} aberto={perfilAberto} aoAbrir={setPerfilAberto} />}
      {aba === "sugestoes" && <Sugestoes equipe={equipe} />}
      {aba === "funcoes" && gestor && <Funcoes />}
    </section>
  );
}

function Visao({ aoAbrir, irSugestoes }: { aoAbrir: (p: string) => void; irSugestoes: () => void }) {
  const [painel, setPainel] = useState<ResumoPerfil[]>([]);
  const [pend, setPend] = useState<{ total: number; por_perfil: Record<string, number> } | null>(null);
  useEffect(() => {
    api<ResumoPerfil[]>("/v1/treinamento/painel").then(setPainel).catch(() => setPainel([]));
    api<{ total: number; por_perfil: Record<string, number> }>("/v1/treinamento/pendencias").then(setPend).catch(() => setPend(null));
  }, []);
  return (
    <>
      {pend && pend.total > 0 && (
        <p className="aviso-paineis" role="status">{pend.total} sugestão(ões) de instrução esperando a sua decisão.{" "}
          <button type="button" className="nx-botao" onClick={irSugestoes}>decidir ›</button></p>
      )}
      <div className="grade-perfis">
        {painel.map((p) => (
          <button key={p.perfil} type="button" className="nx-cartao cartao-perfil" onClick={() => aoAbrir(p.perfil)}>
            <span className="titulo-grupo">{p.perfil}</span>
            <span>{p.versao_publicada ? `versão publicada: v${p.versao_publicada}` : <span className="nx-suave">sem versão publicada</span>}</span>
            <span className="nx-suave">{p.perguntas.perguntas} perguntas analisadas · {p.perguntas.pendentes} sem padrão ainda</span>
            <span className={p.sugestoes.pendente ? "alerta-grupo" : "nx-suave"}>
              sugestões: {p.sugestoes.pendente} pendente(s) · {p.sugestoes.aprovada} aprovada(s) · {p.sugestoes.bloqueada} bloqueada(s)</span>
          </button>
        ))}
        {!painel.length && <p className="nx-suave">Nenhum perfil visível para as suas funções.</p>}
      </div>
    </>
  );
}

function Perfis({ equipe, aberto, aoAbrir }: { equipe: Equipe; aberto: string | null; aoAbrir: (p: string) => void }) {
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [novo, setNovo] = useState({ nome: "", descricao: "" });
  const [aviso, setAviso] = useState<string | null>(null);
  const gestor = equipe.funcoes.includes("gestor_sistema");
  const carregar = useCallback(() => api<Perfil[]>("/v1/treinamento/perfis").then(setPerfis).catch(() => setPerfis([])), []);
  useEffect(() => { void carregar(); }, [carregar]);
  const criar = async () => {
    try { await api("/v1/treinamento/perfis", { corpo: novo }); setNovo({ nome: "", descricao: "" }); setAviso("Perfil criado."); void carregar(); }
    catch (e) { setAviso(`Não foi possível: ${msg(e)}`); }
  };
  return (
    <div className="grade-treinamento">
      <aside className="nx-cartao lista-perfis">
        <h3>Perfis de agente</h3>
        <ul>
          {perfis.map((p) => (
            <li key={p.nome}><button type="button" className={aberto === p.nome ? "ativa" : ""} onClick={() => aoAbrir(p.nome)}>
              <b>{p.nome}</b><small className="nx-suave">{p.versao_publicada ? `v${p.versao_publicada} publicada` : "não publicado"}
                {p.versao_em_andamento ? ` · v${p.versao_em_andamento} em andamento` : ""}</small></button></li>
          ))}
        </ul>
        {gestor && (
          <div className="novo-perfil">
            <input value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} placeholder="novo_perfil" aria-label="Nome do novo perfil" maxLength={41} />
            <input value={novo.descricao} onChange={(e) => setNovo({ ...novo, descricao: e.target.value })} placeholder="descrição" aria-label="Descrição do novo perfil" maxLength={300} />
            <button type="button" className="nx-botao" disabled={!/^[a-z][a-z0-9_]{1,40}$/.test(novo.nome)} onClick={() => void criar()}>criar perfil</button>
          </div>
        )}
        {aviso && <p className="nx-suave" role="status">{aviso}</p>}
      </aside>
      <div>{aberto ? <PerfilDetalhe key={aberto} perfil={aberto} equipe={equipe} aoMudar={() => void carregar()} />
        : <p className="nx-suave">Escolha um perfil.</p>}</div>
    </div>
  );
}

function PerfilDetalhe({ perfil, equipe, aoMudar }: { perfil: string; equipe: Equipe; aoMudar: () => void }) {
  const [versoes, setVersoes] = useState<Versao[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const [rascunho, setRascunho] = useState<Instrucao[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  const [obs, setObs] = useState("");
  const [diff, setDiff] = useState<{ incluidas: Instrucao[]; removidas: Instrucao[]; mantidas: Instrucao[] } | null>(null);
  const [auditoria, setAuditoria] = useState<Auditoria[]>([]);
  const [execucoes, setExecucoes] = useState<Execucao[]>([]);
  const gestor = equipe.funcoes.includes("gestor_sistema");
  const edita = gestor || equipe.funcoes.includes("engenheiro_implantacao") || equipe.validador_de.includes(perfil);
  const publica = gestor || equipe.validador_de.includes(perfil);
  const qa = equipe.funcoes.includes("analista_qa");

  // a lista de versões vem sem as instruções: a versão escolhida é lida inteira
  const abrir = useCallback(async (n: number) => {
    setSel(n);
    setDiff(null);
    const v = await api<Versao>(`/v1/treinamento/perfis/${perfil}/versoes/${n}`).catch(() => null);
    setRascunho(v?.instrucoes ?? []);
  }, [perfil]);
  const carregar = useCallback(async (manter?: number) => {
    const lista = await api<Versao[]>(`/v1/treinamento/perfis/${perfil}/versoes`).catch(() => [] as Versao[]);
    lista.sort((a, b) => b.versao - a.versao);
    setVersoes(lista);
    const alvo = lista.find((v) => v.versao === manter) ?? lista.find((v) => v.estado === "rascunho" || v.estado === "em_teste") ?? lista[0];
    setDiff(null);
    if (alvo) void abrir(alvo.versao); else { setSel(null); setRascunho([]); }
    api<Auditoria[]>(`/v1/treinamento/perfis/${perfil}/auditoria`).then((a) => setAuditoria(a.slice(0, 12))).catch(() => setAuditoria([]));
  }, [perfil, abrir]);
  useEffect(() => { void carregar(); }, [carregar]);
  const versao = versoes.find((v) => v.versao === sel) ?? null;
  const publicada = versoes.find((v) => v.estado === "publicada");
  const carregarExecucoes = useCallback(() => {
    if (!sel) return;
    api<Execucao[]>(`/v1/treinamento/perfis/${perfil}/versoes/${sel}/execucoes`).then(setExecucoes).catch(() => setExecucoes([]));
  }, [perfil, sel]);
  useEffect(() => { carregarExecucoes(); }, [carregarExecucoes, versoes]);
  const execucaoOk = execucoes[0]?.aprovada === true;

  const executar = async (rota: string, corpo?: unknown, metodo?: string, sucesso?: string) => {
    try {
      const r = await api<Versao | undefined>(`/v1/treinamento/perfis/${perfil}/${rota}`, { metodo: metodo ?? "POST", corpo });
      setAviso(sucesso ?? "feito");
      await carregar(r && "versao" in r ? r.versao : sel ?? undefined);
      aoMudar();
    } catch (e) { setAviso(`Não foi possível: ${msg(e)}`); }
  };
  const comparar = async () => {
    if (!versao || !publicada || versao.versao === publicada.versao) return;
    setDiff(await api(`/v1/treinamento/perfis/${perfil}/comparar?de=${publicada.versao}&para=${versao.versao}`));
  };

  return (
    <div className="nx-cartao detalhe-perfil">
      <h3>Perfil {perfil}</h3>
      <div className="linha-versoes" role="group" aria-label="Versões">
        {versoes.map((v) => (
          <button key={v.versao} type="button" className={`nx-botao${v.versao === sel ? " ativo" : ""}`}
            onClick={() => void abrir(v.versao)}>v{v.versao} · {ESTADO_VERSAO[v.estado] ?? v.estado}</button>
        ))}
        {edita && !versoes.some((v) => v.estado === "rascunho" || v.estado === "em_teste") && (
          <button type="button" className="nx-botao nx-botao-principal" onClick={() => void executar(`versoes${publicada ? `?base=${publicada.versao}` : ""}`, undefined, "POST", "Nova versão criada a partir da publicada.")}>+ nova versão</button>
        )}
      </div>
      {aviso && <p className="aviso-paineis" role="status">{aviso}</p>}
      {versao && (
        <>
          <p className="nx-suave">criada por {versao.criado_por} em {new Date(versao.criado_em).toLocaleString("pt-BR")}
            {versao.testes_por && ` · testes ${versao.testes_aprovados ? "aprovados" : "reprovados"} por ${versao.testes_por}${versao.testes_observacao ? ` (“${versao.testes_observacao}”)` : ""}`}
            {versao.publicado_por && ` · publicada por ${versao.publicado_por}`}</p>
          <h4>Instruções ({rascunho.length})</h4>
          <ol className="lista-instrucoes">
            {rascunho.map((ins, i) => (
              <li key={i} className={`instrucao tipo-${ins.tipo}`}>
                {versao.estado === "rascunho" && edita ? (
                  <>
                    <select value={ins.tipo} aria-label={`Tipo da instrução ${i + 1}`}
                      onChange={(e) => setRascunho((r) => r.map((x, k) => (k === i ? { ...x, tipo: e.target.value as Instrucao["tipo"] } : x)))}>
                      <option value="personalidade">personalidade</option><option value="limite">limite</option></select>
                    <input value={ins.texto} maxLength={200} aria-label={`Texto da instrução ${i + 1}`}
                      onChange={(e) => setRascunho((r) => r.map((x, k) => (k === i ? { ...x, texto: e.target.value } : x)))} />
                    <small className={`contador-instrucao${ins.texto.length > 190 ? " perto" : ""}`} aria-label={`${ins.texto.length} de 200 caracteres`}>{ins.texto.length}/200</small>
                    <button type="button" className="nx-botao" aria-label={`Subir instrução ${i + 1}`} disabled={i === 0}
                      onClick={() => setRascunho((r) => { const n = [...r]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}>↑</button>
                    <button type="button" className="nx-botao" aria-label={`Descer instrução ${i + 1}`} disabled={i === rascunho.length - 1}
                      onClick={() => setRascunho((r) => { const n = [...r]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; return n; })}>↓</button>
                    <button type="button" className="nx-botao" aria-label={`Remover instrução ${i + 1}`} onClick={() => setRascunho((r) => r.filter((_, k) => k !== i))}>✕</button>
                  </>
                ) : <><span className="nx-selo">{ins.tipo}</span> {ins.texto}</>}
              </li>
            ))}
          </ol>
          <div className="acoes-painel">
            {versao.estado === "rascunho" && edita && <>
              <button type="button" className="nx-botao" onClick={() => setRascunho((r) => [...r, { tipo: "personalidade", texto: "" }])}>+ instrução</button>
              <button type="button" className="nx-botao" disabled={rascunho.some((r) => !r.texto.trim())}
                onClick={() => void executar(`versoes/${versao.versao}/instrucoes`, { instrucoes: rascunho }, "PUT", "Rascunho salvo.")}>Salvar rascunho</button>
              <button type="button" className="nx-botao nx-botao-principal" onClick={() => void executar(`versoes/${versao.versao}/enviar-teste`, undefined, "POST", "Enviada para teste (QA).")}>Enviar para teste</button></>}
            {versao.estado === "em_teste" && qa && (
              <span className="decisao-painel">
                <input value={obs} onChange={(e) => setObs(e.target.value)} maxLength={1000} placeholder="observação dos testes" aria-label="Observação dos testes" />
                <button type="button" className="nx-botao nx-botao-principal" disabled={!execucaoOk}
                  title={execucaoOk ? "" : "precisa da última execução no ambiente controlado aprovada"}
                  onClick={() => void executar(`versoes/${versao.versao}/testes`, { aprovado: true, observacao: obs }, "POST", "Testes aprovados.")}>Testes aprovados</button>
                <button type="button" className="nx-botao" onClick={() => void executar(`versoes/${versao.versao}/testes`, { aprovado: false, observacao: obs }, "POST", "Testes reprovados.")}>Reprovar</button>
              </span>
            )}
            {versao.estado === "em_teste" && publica && (
              <button type="button" className="nx-botao nx-botao-principal" disabled={!versao.testes_aprovados}
                title={versao.testes_aprovados ? "" : "precisa dos testes aprovados pelo QA"}
                onClick={() => void executar(`versoes/${versao.versao}/publicar`, undefined, "POST", "Versão publicada: os agentes já usam estas instruções.")}>Publicar</button>
            )}
            {publicada && publica && versao.estado === "publicada" && versoes.some((v) => v.estado === "arquivada") && (
              <button type="button" className="nx-botao" onClick={() => void executar("reverter", undefined, "POST", "Revertido para a versão anterior.")}>Reverter para a anterior</button>
            )}
            {publicada && versao.versao !== publicada.versao && <button type="button" className="nx-botao" onClick={() => void comparar()}>comparar com a publicada (v{publicada.versao})</button>}
          </div>
          {versao.estado === "em_teste" && (
            <AmbienteControlado perfil={perfil} versao={versao.versao} execucoes={execucoes} podeCadastrar={edita || qa} aoAtualizar={carregarExecucoes} />
          )}
          {diff && (
            <div className="diff-instrucoes">
              {diff.incluidas.map((i, k) => <p key={`i${k}`} className="incluida">+ [{i.tipo}] {i.texto}</p>)}
              {diff.removidas.map((i, k) => <p key={`r${k}`} className="removida">− [{i.tipo}] {i.texto}</p>)}
              {!diff.incluidas.length && !diff.removidas.length && <p className="nx-suave">sem diferenças</p>}
            </div>
          )}
        </>
      )}
      <h4>Histórico do perfil</h4>
      <ul className="auditoria">{auditoria.map((a, i) => <li key={i}><span className="nx-valor">{new Date(a.instante).toLocaleString("pt-BR")}</span> · {a.autor} · {a.evento.replace(/_/g, " ")}{a.versao ? ` v${a.versao}` : ""}{a.detalhe ? ` — ${a.detalhe.slice(0, 120)}` : ""}</li>)}</ul>
    </div>
  );
}

function Sugestoes({ equipe }: { equipe: Equipe }) {
  const [estado, setEstado] = useState("pendente");
  const [lista, setLista] = useState<Sugestao[]>([]);
  const [edicao, setEdicao] = useState<Record<string, { texto: string; justificativa: string }>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const carregar = useCallback(() => api<Sugestao[]>(`/v1/treinamento/sugestoes?estado=${estado}`).then(setLista).catch(() => setLista([])), [estado]);
  useEffect(() => { void carregar(); }, [carregar]);
  const decide = (perfil: string) => equipe.funcoes.includes("gestor_sistema") || equipe.validador_de.includes(perfil);
  const decidir = async (s: Sugestao, acao: "aprovar" | "editar" | "rejeitar") => {
    const e = edicao[s.id] ?? { texto: s.texto, justificativa: "" };
    try {
      await api(`/v1/treinamento/sugestoes/${s.id}/decisao`, { corpo: { acao, justificativa: e.justificativa,
        ...(acao === "editar" ? { instrucao: { tipo: s.tipo, texto: e.texto } } : {}) } });
      setAviso(acao === "rejeitar" ? "Sugestão rejeitada." : "Sugestão aprovada: entrou no rascunho do perfil (publique pelo fluxo de versões).");
      void carregar();
    } catch (err) { setAviso(`Não foi possível: ${msg(err)}`); }
  };
  return (
    <div className="nx-cartao">
      <h3>Sugestões de instrução <span className="seletor-periodo">{["pendente", "aprovada", "rejeitada", "bloqueada"].map((e) => (
        <button key={e} type="button" className={`nx-botao${estado === e ? " ativo" : ""}`} onClick={() => setEstado(e)}>{e}s</button>))}</span></h3>
      <p className="nx-suave">Geradas pelo analisador a partir das perguntas anonimizadas que os agentes não atendiam bem. Aprovar ou editar coloca a instrução no rascunho do perfil; nada vai aos agentes sem passar por teste e publicação.</p>
      {aviso && <p className="aviso-paineis" role="status">{aviso}</p>}
      <ul className="lista-sugestoes">
        {lista.map((s) => {
          const e = edicao[s.id] ?? { texto: s.texto, justificativa: "" };
          return (
            <li key={s.id} className="nx-cartao sugestao">
              <p><span className="nx-selo">{s.perfil}</span> <span className="nx-selo">{s.tipo}</span> <b>{s.texto_aprovado ?? s.texto}</b></p>
              <p className="nx-suave">{s.motivo} · {s.ocorrencias} ocorrência(s) · {new Date(s.criada_em).toLocaleDateString("pt-BR")}</p>
              {s.bloqueio && <p className="erro-painel">Bloqueada: {s.bloqueio}</p>}
              {s.exemplos.length > 0 && <details><summary>exemplos de perguntas ({s.exemplos.length})</summary><ul>{s.exemplos.map((x, i) => <li key={i}>{x}</li>)}</ul></details>}
              {s.decidido_por && <p className="nx-suave">decidida por {s.decidido_por}{s.justificativa ? `: “${s.justificativa}”` : ""}</p>}
              {s.estado === "pendente" && decide(s.perfil) && (
                <div className="decisao-sugestao">
                  <input value={e.texto} maxLength={200} aria-label="Texto da instrução" onChange={(ev) => setEdicao({ ...edicao, [s.id]: { ...e, texto: ev.target.value } })} />
                  <input value={e.justificativa} maxLength={1000} placeholder="justificativa (obrigatória para rejeitar)" aria-label="Justificativa"
                    onChange={(ev) => setEdicao({ ...edicao, [s.id]: { ...e, justificativa: ev.target.value } })} />
                  <span className="acoes-painel">
                    {e.texto === s.texto
                      ? <button type="button" className="nx-botao nx-botao-principal" onClick={() => void decidir(s, "aprovar")}>Aprovar</button>
                      : <button type="button" className="nx-botao nx-botao-principal" onClick={() => void decidir(s, "editar")}>Aprovar com edição</button>}
                    <button type="button" className="nx-botao" disabled={e.justificativa.trim().length < 3} onClick={() => void decidir(s, "rejeitar")}>Rejeitar</button>
                  </span>
                </div>
              )}
            </li>
          );
        })}
        {!lista.length && <li className="nx-suave">Nenhuma sugestão {estado}.</li>}
      </ul>
    </div>
  );
}

function Funcoes() {
  const [funcoes, setFuncoes] = useState<Funcao[]>([]);
  const [auditoria, setAuditoria] = useState<Auditoria[]>([]);
  const [nova, setNova] = useState({ login: "", funcao: "validador", perfil_agente: "" });
  const [aviso, setAviso] = useState<string | null>(null);
  // US-1805: os integradores vêm da Administração › Usuários (escolha da lista em vez de digitar o login)
  const [integradores, setIntegradores] = useState<{ login: string; nome: string }[]>([]);
  useEffect(() => {
    api<{ itens: { login: string; nome: string }[] }>("/v1/usuarios?perfil=integrador&ativo=true&limite=200")
      .then((r) => setIntegradores(r.itens)).catch(() => setIntegradores([]));
  }, []);
  const carregar = useCallback(() => {
    api<Funcao[]>("/v1/treinamento/funcoes").then(setFuncoes).catch(() => setFuncoes([]));
    api<Auditoria[]>("/v1/treinamento/auditoria?limite=30").then(setAuditoria).catch(() => setAuditoria([]));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);
  const conceder = async () => {
    try {
      await api("/v1/treinamento/funcoes", { corpo: { login: nova.login.trim(), funcao: nova.funcao, perfil_agente: nova.funcao === "validador" ? nova.perfil_agente : null } });
      setAviso("Função concedida."); carregar();
    } catch (e) { setAviso(`Não foi possível: ${msg(e)}`); }
  };
  const revogar = async (f: Funcao) => {
    try { await api(`/v1/treinamento/funcoes/${f.id}`, { metodo: "DELETE" }); setAviso("Função revogada."); carregar(); }
    catch (e) { setAviso(`Não foi possível: ${msg(e)}`); }
  };
  return (
    <div className="grade-treinamento">
      <div className="nx-cartao">
        <h3>Equipe e funções</h3>
        <table className="tabela-composicao">
          <thead><tr><th>Login</th><th>Função</th><th>Desde</th><th /></tr></thead>
          <tbody>{funcoes.map((f) => (
            <tr key={f.id}><td>{f.login}</td><td>{NOME_FUNCAO[f.funcao] ?? f.funcao}{f.perfil_agente ? ` · ${f.perfil_agente}` : ""}</td>
              <td>{new Date(f.concedido_em).toLocaleDateString("pt-BR")}</td>
              <td><button type="button" className="nx-botao" onClick={() => void revogar(f)}>revogar</button></td></tr>
          ))}</tbody>
        </table>
        <div className="novo-perfil">
          <input value={nova.login} onChange={(e) => setNova({ ...nova, login: e.target.value })} placeholder="login (perfil integrador)" aria-label="Login"
            list="integradores-nexora" />
          <datalist id="integradores-nexora">{integradores.map((i) => <option key={i.login} value={i.login}>{i.nome}</option>)}</datalist>
          <select value={nova.funcao} onChange={(e) => setNova({ ...nova, funcao: e.target.value })} aria-label="Função">
            {Object.entries(NOME_FUNCAO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          {nova.funcao === "validador" && <input value={nova.perfil_agente} onChange={(e) => setNova({ ...nova, perfil_agente: e.target.value })} placeholder="perfil do agente" aria-label="Perfil do agente" />}
          <button type="button" className="nx-botao" disabled={nova.login.trim().length < 3} onClick={() => void conceder()}>conceder</button>
        </div>
        {aviso && <p className="nx-suave" role="status">{aviso}</p>}
        <p className="nx-suave">Pessoa nova na equipe? Crie a conta em <Link to="/admin/usuarios/novo">Administração › Usuários</Link> (perfil Integrador {marcaAtual().empresa}) e depois conceda a função aqui.</p>
      </div>
      <div className="nx-cartao">
        <h3>Auditoria geral</h3>
        <ul className="auditoria">{auditoria.map((a, i) => <li key={i}><span className="nx-valor">{new Date(a.instante).toLocaleString("pt-BR")}</span> · {a.autor} · {a.evento.replace(/_/g, " ")}{a.perfil ? ` · ${a.perfil}` : ""}{a.versao ? ` v${a.versao}` : ""}</li>)}</ul>
      </div>
    </div>
  );
}

/** Ambiente controlado (GUS-1008): perguntas-modelo do perfil e execuções da versão candidata contra a simulação. */
function AmbienteControlado({ perfil, versao, execucoes, podeCadastrar, aoAtualizar }: {
  perfil: string; versao: number; execucoes: Execucao[]; podeCadastrar: boolean; aoAtualizar: () => void;
}) {
  const [perguntas, setPerguntas] = useState<PerguntaModelo[]>([]);
  const [nova, setNova] = useState<{ pergunta: string; esperado: PerguntaModelo["esperado"] }>({ pergunta: "", esperado: "responder" });
  const [aviso, setAviso] = useState<string | null>(null);
  const carregar = useCallback(() => api<PerguntaModelo[]>(`/v1/treinamento/perfis/${perfil}/perguntas-modelo`).then(setPerguntas).catch(() => setPerguntas([])), [perfil]);
  useEffect(() => { void carregar(); }, [carregar]);
  const criar = async () => {
    try { await api(`/v1/treinamento/perfis/${perfil}/perguntas-modelo`, { corpo: { pergunta: nova.pergunta.trim(), esperado: nova.esperado } });
      setNova({ pergunta: "", esperado: "responder" }); setAviso("Pergunta-modelo cadastrada."); void carregar(); }
    catch (e) { setAviso(`Não foi possível: ${msg(e)}`); }
  };
  const desativar = async (id: string) => {
    try { await api(`/v1/treinamento/perguntas-modelo/${id}`, { metodo: "DELETE" }); void carregar(); }
    catch (e) { setAviso(`Não foi possível: ${msg(e)}`); }
  };
  const ultima = execucoes[0];
  return (
    <div className="ambiente-controlado">
      <h4>Ambiente controlado (operadora de simulação)</h4>
      <p className="nx-suave">O QA roda a versão candidata contra a simulação: <code>python -m treinamento.sandbox executar --perfil {perfil} --versao {versao}</code>.
        A execução aprovada (≥ 3 evals de segurança e todas as perguntas-modelo com o comportamento esperado) libera "Testes aprovados".</p>
      <div className="grade-micro">
        <div className="bloco-micro">
          <h5>Perguntas-modelo ({perguntas.length})</h5>
          <ul className="auditoria">{perguntas.map((q) => (
            <li key={q.id}>{q.pergunta} <span className="nx-selo">{q.esperado}</span>
              {podeCadastrar && <button type="button" className="nx-botao" aria-label="Desativar pergunta-modelo" onClick={() => void desativar(q.id)}>✕</button>}</li>
          ))}</ul>
          {!perguntas.length && <p className="erro-painel">Sem perguntas-modelo: a execução não pode ser aprovada.</p>}
          {podeCadastrar && (
            <div className="novo-perfil">
              <input value={nova.pergunta} onChange={(e) => setNova({ ...nova, pergunta: e.target.value })} maxLength={500} placeholder="pergunta típica deste perfil" aria-label="Nova pergunta-modelo" />
              <select value={nova.esperado} onChange={(e) => setNova({ ...nova, esperado: e.target.value as PerguntaModelo["esperado"] })} aria-label="Comportamento esperado">
                <option value="responder">responder</option><option value="limitar">limitar</option><option value="recusar">recusar</option></select>
              <button type="button" className="nx-botao" disabled={nova.pergunta.trim().length < 5} onClick={() => void criar()}>cadastrar</button>
            </div>
          )}
        </div>
        <div className="bloco-micro">
          <h5>Execuções da v{versao} ({execucoes.length}) <button type="button" className="nx-botao" onClick={aoAtualizar}>atualizar</button></h5>
          {ultima ? (
            <p className={ultima.aprovada ? "" : "erro-painel"}>Última: <b>{ultima.aprovada ? "aprovada" : "reprovada"}</b>
              {ultima.motivo && ultima.motivo !== "aprovada" ? ` — ${ultima.motivo}` : ""}</p>
          ) : <p className="nx-suave">Nenhuma execução registrada para esta versão.</p>}
          <ul className="auditoria">{execucoes.map((x) => (
            <li key={x.id}><span className="nx-valor">{new Date(x.instante).toLocaleString("pt-BR")}</span> · {x.executado_por} · {x.empresa_sandbox}
              {" "}· evals {x.evals_seguranca.filter((e) => e.aprovado).length}/{x.evals_seguranca.length}
              {" "}· perguntas {x.perguntas.filter((q) => q.aprovado).length}/{x.perguntas.length} · {x.aprovada ? "aprovada" : "reprovada"}</li>
          ))}</ul>
        </div>
      </div>
      {aviso && <p className="nx-suave" role="status">{aviso}</p>}
    </div>
  );
}
